import { db } from '@/config/firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { getCurrentEstTimeKey } from '@/utils/timeUtils';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

export interface DaypartingMultiplierEntry {
  id: string;
  finalMultiplier: number | null;
}

export interface DaypartingDashboardData {
  enableDayparting: string | null;
  useFarmingGroupMultipliers: string | null;
  useGlobalMultipliers: string | null;
  currentTimeKey: string;
  currentTimeMultiplier: number | null;
  totalMultipliers: number;
  positiveMultipliers: number;
  negativeMultipliers: number;
  oldValues: Record<string, string | null>;
  allMultipliers: DaypartingMultiplierEntry[];
  dashboardUrl: string | null;
}

export async function getDaypartingData(companyName: string, asin: string): Promise<DaypartingDashboardData | null> {
  try {
    const dataDocRef = doc(db, 'pmt2_data', companyName, 'products', asin);
    const dataSnap = await getDoc(dataDocRef);
    const data = dataSnap.exists() ? dataSnap.data() : {};

    const oldValuesMap = (await fetchLatestOldValuesMap(companyName, asin)) || {};

    const multipliersColRef = collection(db, 'pmt2_dayparting_multipliers', companyName, 'asins', asin, 'entries');
    const multipliersSnap = await getDocs(multipliersColRef);

    const allMultipliers: DaypartingMultiplierEntry[] = [];
    let total = 0;
    let positive = 0;
    let negative = 0;

    multipliersSnap.forEach((docSnap) => {
      const entry = docSnap.data();
      const finalMultiplier = entry.finalMultiplier ?? null;
      allMultipliers.push({
        id: docSnap.id,
        finalMultiplier: typeof finalMultiplier === 'number' ? finalMultiplier : null,
      });
      total++;
      if (typeof finalMultiplier === 'number') {
        if (finalMultiplier > 1) positive++;
        else if (finalMultiplier < 1) negative++;
      }
    });

    const currentTimeKey = getCurrentEstTimeKey().replace('|', '_');
    const currentEntry = allMultipliers.find((e) => e.id === currentTimeKey);
    const currentTimeMultiplier = currentEntry?.finalMultiplier ?? null;

    const urlDocRef = doc(db, 'pmt2_clienturldatabase', companyName, 'items', 'Dayparting Bid Calculations');
    const urlSnap = await getDoc(urlDocRef);
    const urlData = urlSnap.exists() ? urlSnap.data() : {};
    const dashboardUrl = urlData.value || urlData.url || null;

    return {
      enableDayparting: data['Enable Dayparting'] ?? null,
      useFarmingGroupMultipliers: data['Allow Use of Farming Group Dayparting Multipliers'] ?? null,
      useGlobalMultipliers: data['Allow Use of Global Dayparting Multipliers'] ?? null,
      currentTimeKey: getCurrentEstTimeKey(),
      currentTimeMultiplier,
      totalMultipliers: total,
      positiveMultipliers: positive,
      negativeMultipliers: negative,
      oldValues: {
        'Enable Dayparting': oldValuesMap['Enable Dayparting'] ?? null,
        'Use Farming Group Multipliers': oldValuesMap['Use Farming Group Multipliers'] ?? null,
        'Use Global Multipliers': oldValuesMap['Use Global Multipliers'] ?? null,
        'Total Multipliers': oldValuesMap['Total Multipliers'] ?? null,
        'Positive Multipliers': oldValuesMap['Positive Multipliers'] ?? null,
        'Negative Multipliers': oldValuesMap['Negative Multipliers'] ?? null,
      },
      allMultipliers,
      dashboardUrl,
    };
  } catch (error) {
    console.error('Error fetching Dayparting dashboard data:', error);
    return null;
  }
}